import React from 'react';
import { useTranslation } from 'react-i18next';
import { TimelineEvent } from 'types/timeline';
import { getActivityEventTypeConfig } from '../../../config/activityEventTypes';
import NotificationTimestampAtom from '../../atoms/NotificationTimestamp/NotificationTimestampAtom';
import './ActivityCardMolecule.scss';

export interface ActivityCardMoleculeProps {
  event: TimelineEvent;
  onClick?: (event: TimelineEvent) => void;
  className?: string;
}

/**
 * Build interpolation params from event payload for i18n translation
 */
function getInterpolationParams(event: TimelineEvent): Record<string, string> {
  const payload = event.payload || {};
  const actor = event.actor || '';

  // Keyed on `LedgerEntryType` (EAINT-764 Stage 2 / OQ-3). The previous switch matched the legacy
  // PascalCase vocabulary the API stopped sending, so every case fell to `default` and the activity
  // feed interpolated only `actor` — labels rendered with empty placeholders.
  //
  // Several old arms had no ledger counterpart at all and are gone rather than remapped:
  // ArcActivated/Completed/Cancelled/Failed collapse into `arc_lifecycle`, RoleMemberAdded/Removed
  // into `permission_changed`, LifecycleTransitioned into `arc_transition`, and AttachmentUpdated
  // never had a writer.
  switch (event.eventType) {
    case 'sphere_created':
      return { actor };
    case 'sphere_property_changed':
      return { actor, property: payload.propertyName || '' };
    case 'status_propagated':
      return { prev: payload.previousStatus || '', new: payload.newStatus || '' };
    case 'check_in':
      return { actor, factorName: payload.factorName || '', statusValue: payload.statusValue || '' };
    case 'arc_lifecycle':
      return {
        name: payload.templateSystemName || payload.arcName || payload.name || '',
        // Six legacy types (ArcDeployed/Completed/Cancelled/Failed/Activated/LifecycleChanged)
        // collapse into this one, so the specific state now comes from the payload rather than
        // from the event type.
        lifecycle: payload.lifecycle || '',
      };
    case 'arc_transition':
      return {
        fromStage: payload.fromStageName || payload.fromStage || '',
        toStage: payload.toStageName || payload.toStage || '',
      };
    case 'stage_action':
      return { actor, action: payload.actionId || payload.actionType || '' };
    case 'task_completed':
      return { actor, taskName: payload.taskName || '' };
    case 'factor_lifecycle':
      return { actor, factorType: payload.factorType || '', transition: payload.transition || '' };
    case 'perspective_assigned':
    case 'perspective_unassigned':
      return { subject: payload.subjectId || '', perspective: payload.perspectiveSystemName || '' };
    case 'permission_changed':
      return { relation: payload.relation || '', subjectType: payload.subjectType || '' };
    case 'aspect_observation':
      return { actor, aspect: payload.sourceAspect || '' };
    case 'aspect_validation':
      return { actor, status: payload.status || '' };
    case 'aspect_attached':
    case 'aspect_detached':
      return { actor, aspect: payload.aspectSystemName || '' };
    case 'attachment_added':
    case 'attachment_removed':
      return { actor, name: payload.name || '' };
    case 'velocity_changed':
      return { prev: payload.oldVelocity ?? '', new: payload.newVelocity ?? '' };
    case 'impact_changed':
      return { prev: payload.oldImpact ?? '', new: payload.newImpact ?? '' };
    default:
      return { actor };
  }
}

const ActivityCardMolecule: React.FC<ActivityCardMoleculeProps> = ({
  event,
  onClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const config = getActivityEventTypeConfig(event.eventType);
  const params = getInterpolationParams(event);

  const handleClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <button
      type="button"
      className={`activity-card-molecule ${className}`}
      onClick={handleClick}
    >
      <div className="activity-card-icon">
        <i className={`pi ${config.icon}`} />
      </div>
      <div className="activity-card-content">
        <span className="activity-card-text">
          {t(config.labelKey, params)}
        </span>
      </div>
      <div className="activity-card-timestamp">
        <NotificationTimestampAtom timestamp={event.timestamp} />
      </div>
    </button>
  );
};

export default ActivityCardMolecule;
